<script>
import { styleState } from "@app/store/styleStore.svelte.js";
import {
    modalState, hideModal
} from "@app/store/modalStore.svelte.js";
import { lockState } from "@app/store/lockStore.svelte.js";
import {
    externalDataState
} from "@app/store/externalDataStore.svelte.js";
import {
    projectState
} from "@app/store/projectStore.svelte.js";
import {
    eventGroupState
} from "@app/store/eventGroupStore.svelte.js";
import {
    markerState
} from "@app/store/markerStore.svelte.js";
import {
    toast } from '@zerodevx/svelte-toast';
import {
    fade
} from "svelte/transition";
import ISO6391 from 'iso-639-1';
import Swal from 'sweetalert2'
import { SWAL_BASE, SWAL_CLASSES } from '@app/lib/swalClasses.js';
import * as m from "@app/paraglide/messages.js";
import { onMount } from "svelte";

import orderByTime from "@cclib/functions/eventGroups/orderByTime.js";
import removeHtmlEntities from "@cclib/functions/eventGroups/removeHtmlEntities.js";

/* Firebase */
import { auth, functions } from '@app/configs/firebase.js';
import storage from '@app/configs/storage.js';
import { ref, uploadString } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';

let languages = ISO6391.getAllCodes();
let uploadBasePath = "users/" + auth.currentUser.uid + "/iconik/";
let iconikStorages = externalDataState.storages || [],
    iconikStorage = $state(iconikStorages[0]?.id), 
    profile = $state("webVtt"),
    publishing = $state(false),
    ignoreAll = false,
    progress = $state(0),    
    statusMsg = $state(m.iconik_publish_status_starting()),
    publishMarkers = $state(false),
    selectedMarkerListIds = $state([]),
    requestReview = $state(false),
    selectedUserIds = $state([]),
    selectedGroupIds = $state([]),
    externalEmails = $state(""),
    minApprovals = $state(1),
    reviewersLoading = $state(false),
    reviewersError = $state(""),
    reviewerUsers = $state([]),
    reviewerGroups = $state([]);

onMount(() => {
    selectedMarkerListIds = defaultMarkerListIds();
    loadReviewers();
});

function markerListsWithItems() {
    return (markerState.lists || []).filter((list) => (list.markers || []).length > 0);
}

function defaultMarkerListIds() {
    return markerListsWithItems().map((list) => list.id);
}

function onPublishMarkersToggle() {
    if (publishMarkers && selectedMarkerListIds.length === 0) {
        selectedMarkerListIds = defaultMarkerListIds();
    }
}

async function loadReviewers() {
    reviewersLoading = true;
    reviewersError = "";
    try {
        let res = await httpsCallable(functions, "v8IconikListReviewers")({});
        if (res.data?.result === "failed") {
            reviewersError = res.data.message || m.iconik_publish_reviewers_error();
            return;
        }
        reviewerUsers = res.data?.users || [];
        reviewerGroups = res.data?.groups || [];
    } catch (err) {
        console.log(err);
        reviewersError = m.iconik_publish_reviewers_error();
    } finally {
        reviewersLoading = false;
    }
}

async function updateStatus(p, msg) {
    progress = p;
    statusMsg = msg;
}

async function cancelExport(eventGroupId, eventId) {
    toast.push(m.iconik_publish_export_aborted_toast(), {classes: ['toast-warning']});

    projectState.selected = eventGroupId;
    eventGroupState[projectState.selected].selected = [eventId];    setTimeout(() => {
        const selectedIndex = eventGroupState[projectState.selected].selected[0];
        window.dispatchEvent(new CustomEvent('scrollToVirtualListItem', {
            detail: { index: selectedIndex }
        }));

        if (lockState.video && player){
            player.currentTime = eventGroupState[projectState.selected].events[selectedIndex].start;
        }
    }, 250);

    hideModal();
}

function alertUser(msg) {
    let response = Swal.fire({
        ...SWAL_BASE,
        titleText: m.iconik_publish_precheck_title(),
        text: msg,
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonText: m.iconik_publish_precheck_abort(),
        denyButtonText: m.iconik_publish_precheck_ignore(),
        cancelButtonText: m.iconik_publish_precheck_ignore_all(),
        allowOutsideClick: false,
        allowEscapeKey: false,
        customClass: SWAL_CLASSES.precheck,
    }).then((result) => {
        if (result.isConfirmed) {
            return true;
        } else if (result.isDenied) {
            return false;
        } else if (result.isDismissed) {
            ignoreAll = true;
            return false;
        }
    });

    return response;
}

async function preExportCheck(events) {
    for (let i = 0; i < events.length; i++) {
        if (!ignoreAll) {
            if (events[i].text) {
                if (isNaN(events[i].start)) {
                    if (await alertUser(m.iconik_publish_precheck_missing_start({ index: i + 1 }))) {
                        return {
                            err: true,
                            eventId: i
                        }
                    };
                } else if (isNaN(events[i].end)) {
                    if (await alertUser(m.iconik_publish_precheck_missing_end({ index: i + 1 }))) {
                        return {
                            err: true,
                            eventId: i
                        }
                    };
                } else if (events[i].start === events[i].end) {
                    if (await alertUser(m.iconik_publish_precheck_same_time({ index: i + 1 }))) {
                        return {
                            err: true,
                            eventId: i
                        }
                    };
                } else if (events[i].start > events[i].end) {
                    if (await alertUser(m.iconik_publish_precheck_start_after_end({ index: i + 1 }))) {
                        return {
                            err: true,
                            eventId: i
                        }
                    };
                } else if (i > 0 && events[i].start < events[i - 1].end) {
                    /* Adding check in case overlap is less than a frame */
                    if (events[i - 1].end - events[i].start < 1 / projectState.frameRate) {
                        events[i].start = events[i - 1].end;
                    } else if (await alertUser(m.iconik_publish_precheck_overlap({ index: i + 1 }))) {
                        return {
                            err: true,
                            eventId: i
                        }
                    };
                }
            } else {
                if (await alertUser(m.iconik_publish_precheck_no_text({ index: i + 1 }))) {
                    return {
                        err: true,
                        eventId: i
                    }
                };
            }
        }

    }

    return {
        err: false
    }
}

function flattenMarkersForPublish() {
    let selected = new Set(selectedMarkerListIds);
    let markers = [];
    (markerState.lists || []).forEach((list) => {
        if (!selected.has(list.id)) return;
        (list.markers || []).forEach((marker) => {
            markers.push({
                id: marker.id,
                listId: list.id,
                listName: list.name,
                color: list.color,
                time: marker.time,
                endTime: marker.endTime,
                comment: marker.comment || "",
                extId: marker.extId || null
            });
        });
    });
    return markers;
}

function parseExternalEmails(value) {
    return (value || "")
        .split(/[,;\s]+/)
        .map((email) => email.trim())
        .filter((email) => email.includes("@"));
}

async function publish() {
    try {
        publishing = true;
        ignoreAll = false;

        let tracksToPublish = eventGroupState.filter((evg) => evg.publish);
        if (tracksToPublish.length === 0 && !publishMarkers && !requestReview) {
            throw new Error(m.iconik_publish_nothing_selected_error());
        }

        let completed = {
            subtitles: false,
            markers: false,
            review: false
        };

        if (tracksToPublish.length > 0) {
            let selectedStorage = iconikStorages.find(s => {
                return s.id === iconikStorage;
            });

            if (!selectedStorage) {
                throw new Error(m.iconik_publish_storage_error());
            }

            let storageMethod = selectedStorage.method;

            updateStatus(10, m.iconik_publish_status_creating_project());
            let projectJson = JSON.parse(JSON.stringify(projectState));
            projectJson.eventGroups = [];
            for (let i = 0; i<eventGroupState.length; i++){

                let copyOfEventGroup = JSON.parse(JSON.stringify(eventGroupState[i]));
                copyOfEventGroup = orderByTime(copyOfEventGroup);
                copyOfEventGroup = removeHtmlEntities(copyOfEventGroup);
                if (copyOfEventGroup.publish){
                    let preCheckResult = await preExportCheck(copyOfEventGroup.events);
                    if (preCheckResult.err) {
                        cancelExport(i, preCheckResult.eventId);
                        return;
                    } else {
                        if (!copyOfEventGroup.iconikOverwrite){
                            copyOfEventGroup.extId = "";
                        }
                        projectJson.eventGroups.push(copyOfEventGroup);
                    }
                }            
            }

            if (projectJson.eventGroups.length === 0 && !publishMarkers && !requestReview) {
                return;
            }

            if (projectJson.eventGroups.length > 0) {
                updateStatus(30, m.iconik_publish_status_saving_project());
                let storageRef = ref(storage);
                await uploadString(ref(storageRef, uploadBasePath + projectState.id), JSON.stringify(projectJson));
                updateStatus(50, m.iconik_publish_status_creating_job());
                let publishRes = await httpsCallable(functions, "v8IconikPublish")({
                    storageId: iconikStorage,
                    projectId: projectState.id,
                    storageMethod: storageMethod || "GCS",
                    filePath: uploadBasePath + projectState.id,
                    assetId: externalDataState.assetId,
                    profile: profile,
                    window: {
                        width: styleState.width,
                        height: styleState.height,
                        xPadding: styleState.xPadding,
                        yPadding: styleState.yPadding
                    }
                });

                console.log(publishRes);

                if (publishRes.data && publishRes.data.result !== "passed") {
                    throw new Error(m.iconik_publish_failed_error({ message: publishRes.data.message }));
                }
                completed.subtitles = true;
            }
        }

        if (publishMarkers) {
            let markersToPublish = flattenMarkersForPublish();
            if (selectedMarkerListIds.length === 0 || markersToPublish.length === 0) {
                throw new Error(m.iconik_publish_markers_lists_required());
            }
            updateStatus(70, m.iconik_publish_status_publishing_markers());
            try {
                let markersRes = await httpsCallable(functions, "v8IconikPublishMarkers")({
                    assetId: externalDataState.assetId,
                    markers: markersToPublish,
                    listIds: selectedMarkerListIds
                });
                if (markersRes.data && markersRes.data.result !== "passed") {
                    throw new Error(markersRes.data.message || "Marker publish failed");
                }
                completed.markers = true;
                toast.push(m.iconik_publish_markers_success_toast(), {classes: ['toast-success']});
            } catch (markerErr) {
                console.log(markerErr);
                toast.push(m.iconik_publish_markers_failed_toast({ error: markerErr.message }), {classes: ['toast-warning']});
            }
        }

        if (requestReview) {
            updateStatus(85, m.iconik_publish_status_requesting_review());
            try {
                let approvalRes = await httpsCallable(functions, "v8IconikRequestApproval")({
                    assetId: externalDataState.assetId,
                    users: selectedUserIds,
                    groups: selectedGroupIds,
                    externals: parseExternalEmails(externalEmails),
                    min_number: Number(minApprovals) || 1
                });
                if (approvalRes.data && approvalRes.data.result !== "passed") {
                    throw new Error(approvalRes.data.message || "Approval request failed");
                }
                completed.review = true;
                toast.push(m.iconik_publish_review_success_toast(), {classes: ['toast-success']});
            } catch (reviewErr) {
                console.log(reviewErr);
                toast.push(m.iconik_publish_review_failed_toast({ error: reviewErr.message }), {classes: ['toast-warning']});
            }
        }

        updateStatus(95, m.iconik_publish_status_cleaning_up());

        if (completed.subtitles) {
            toast.push(m.iconik_publish_success_toast(), {classes: ['toast-success']});
        }

        updateStatus(100, m.iconik_publish_status_done());

        let summaryParts = [];
        if (completed.subtitles) summaryParts.push(m.iconik_publish_summary_subtitles());
        if (completed.markers) summaryParts.push(m.iconik_publish_summary_markers());
        if (completed.review) summaryParts.push(m.iconik_publish_summary_review());

        await Swal.fire({
            ...SWAL_BASE,
            titleText: m.iconik_publish_success_title(),
            text: summaryParts.length
                ? m.iconik_publish_success_text_with_actions({ summary: summaryParts.join(" ") })
                : m.iconik_publish_success_text(),
            showDenyButton: false,
            showCancelButton: false,
            confirmButtonText: m.iconik_publish_success_confirm(),            
            allowOutsideClick: false,
            allowEscapeKey: false,
            customClass: SWAL_CLASSES.ok,
        });

        hideModal();
    } catch (err) {
        console.log(err, err.message);
        toast.push(m.iconik_publish_failed_toast({ error: err.message }), {classes: ['toast-danger']});
    } finally {
        publishing = false;
        statusMsg = "";
        progress = 0;
    }
}

function togglePublishAll() {
    eventGroupState.forEach((evg, index, eventGroups) => {
        eventGroups[index].publish = !evg.publish;
    });
}

function toggleClosedCaptionAll() {
    eventGroupState.forEach((evg, index, eventGroups) => {
        eventGroups[index].cc = !evg.cc;
    });
}

function toggleOverwriteAll(){
    eventGroupState.forEach((evg, index, eventGroups) => {
        eventGroups[index].iconikOverwrite = !evg.iconikOverwrite;
    });
}
</script>

<div 
    transition:fade={{ duration: 100 }} 
    class="modal {modalState.selected === 'iconikPublish' ? 'show d-block' : ''}" 
    role="dialog" 
    aria-labelledby="iconikPublishTitle"
    aria-describedby="iconikPublishDescription"
    tabindex="-1" 
    id="IconikPublishModal">
    <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title" id="iconikPublishTitle">
                    <img width="23px" src="./assets/img/logos/iconik-icon.ico" alt={m.iconik_publish_icon_alt()}> {m.iconik_publish_title()}
                </h4>
                <button type="button" 
                        class="btn-close" 
                        id="closeIconikPublishModal"
                        aria-label={m.iconik_publish_close_label()} 
                        onclick={hideModal}></button>
            </div>
            <div class="modal-body" id="iconikPublishDescription">
                <div class="row">
                    <div class="col-6 mb-3">
                        {#if iconikStorages.length > 0}
                        <div>
                            <label class="form-label" for="iconikStorageselect">{m.iconik_publish_storage_label()}</label>
                            <select class="form-select" disabled={publishing} bind:value={iconikStorage}>
                                {#each iconikStorages as storageOption (storageOption.id)}
                                <option value={storageOption.id}>{storageOption.name}</option>
                                {/each}
                            </select>
                        </div>
                        {:else}
                        <p class="text-danger small">{m.iconik_publish_storage_error()}</p>
                        {/if}
                    </div>
                    <div class="col-6 mb-3">
                        <div>
                            <label class="form-label" for="profileSelect">{m.iconik_publish_format_label()}</label>
                            <select class="form-select" disabled={publishing} bind:value={profile}>
                                <option>webVtt</option>
                                <option>subRip</option>
                                <option>scenerist</option>
                                <option>ebuStl</option>                          
                            </select>
                        </div>
                    </div>
                    <div class="col-12 mb-2 d-flex align-items-end justify-content-end">
                        <div class="btn-group btn-group-sm" role="group">
                            <button type="button" disabled={publishing} class="btn btn-outline-primary" title={m.iconik_publish_publish_all_title()} onclick={() => togglePublishAll()}><i class="bi bi-check"></i> {m.iconik_publish_publish_all_button()}</button>
                            <button type="button" disabled={publishing} class="btn btn-outline-primary" title={m.iconik_publish_cc_all_title()}  onclick={() => toggleClosedCaptionAll()}><i class="bi bi-check"></i> {m.iconik_publish_cc_all_button()}</button>
                            <button type="button" disabled={publishing} class="btn btn-outline-primary" title={m.iconik_publish_overwrite_all_title()}  onclick={() => toggleOverwriteAll()}><i class="bi bi-check"></i> {m.iconik_publish_overwrite_all_button()}</button>
                        </div>
                    </div>
                </div>

                <div class="table-responsive" style="max-height: 500px;">
                    <table class="table">
                        <thead>
                            <tr>
                                <th scope="col">{m.iconik_publish_event_group_header()}</th>
                                <th scope="col">{m.iconik_publish_language_header()}</th>
                                <th scope="col"><i class="bi bi-badge-cc"></i></th>
                                <th scope="col">{m.iconik_publish_publish_header()}</th>
                                <th scope="col">{m.iconik_publish_overwrite_header()}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {#each eventGroupState as eventGroupOption (eventGroupOption.id)}
                            <tr>
                                <th>{eventGroupOption.name}</th>
                                <td>
                                    <select disabled={publishing} class="form-select" bind:value={eventGroupOption.language}>
                                        {#each languages as languageOption, index(index)}
                                        <option>{languageOption}</option>
                                        {/each}
                                    </select>
                                </td>
                                <td>
                                    <input class="form-check-input" disabled={publishing} type="checkbox" bind:checked={eventGroupOption.cc}>
                                </td>
                                <td>
                                    <input class="form-check-input" disabled={publishing} type="checkbox" bind:checked={eventGroupOption.publish}>
                                </td>
                                <td>
                                    <input class="form-check-input" disabled={publishing} type="checkbox" bind:checked={eventGroupOption.iconikOverwrite}>
                                </td>
                            </tr>
                            {/each}
                        </tbody>
                    </table>
                </div>

                <hr class="my-3" />

                <div class="mb-3 form-check">
                    <input class="form-check-input" type="checkbox" id="publishMarkersCheck" disabled={publishing} bind:checked={publishMarkers} onchange={onPublishMarkersToggle}>
                    <label class="form-check-label" for="publishMarkersCheck">{m.iconik_publish_markers_label()}</label>
                    <div class="form-text">{m.iconik_publish_markers_help()}</div>
                </div>

                {#if publishMarkers}
                <div class="mb-3">
                    <label class="form-label" for="markerListsSelect">{m.iconik_publish_markers_lists_label()}</label>
                    <select multiple class="form-select" id="markerListsSelect" disabled={publishing} size="5" bind:value={selectedMarkerListIds}>
                        {#each markerListsWithItems() as list (list.id)}
                        <option value={list.id}>{list.name} ({list.markers.length})</option>
                        {/each}
                    </select>
                    <div class="form-text">{m.iconik_publish_markers_lists_help()}</div>
                </div>
                {/if}

                <div class="mb-3 form-check">
                    <input class="form-check-input" type="checkbox" id="requestReviewCheck" disabled={publishing} bind:checked={requestReview}>
                    <label class="form-check-label" for="requestReviewCheck">{m.iconik_publish_request_review_label()}</label>
                    <div class="form-text">{m.iconik_publish_request_review_help()}</div>
                </div>

                {#if requestReview}
                <div class="row border rounded p-3 mx-0 mb-2 bg-light">
                    {#if reviewersLoading}
                    <p class="small text-muted mb-0">{m.iconik_publish_reviewers_loading()}</p>
                    {:else}
                        {#if reviewersError}
                        <p class="small text-warning">{reviewersError}</p>
                        {/if}
                        <div class="col-md-6 mb-3">
                            <label class="form-label" for="reviewerUsersSelect">{m.iconik_publish_reviewers_users_label()}</label>
                            <select multiple class="form-select" id="reviewerUsersSelect" disabled={publishing} size="5" bind:value={selectedUserIds}>
                                {#each reviewerUsers as user (user.id)}
                                <option value={user.id}>{user.label}{user.email ? ` (${user.email})` : ""}</option>
                                {/each}
                            </select>
                        </div>
                        <div class="col-md-6 mb-3">
                            <label class="form-label" for="reviewerGroupsSelect">{m.iconik_publish_reviewers_groups_label()}</label>
                            <select multiple class="form-select" id="reviewerGroupsSelect" disabled={publishing} size="5" bind:value={selectedGroupIds}>
                                {#each reviewerGroups as group (group.id)}
                                <option value={group.id}>{group.label}</option>
                                {/each}
                            </select>
                        </div>
                        <div class="col-md-8 mb-3">
                            <label class="form-label" for="externalEmailsInput">{m.iconik_publish_reviewers_externals_label()}</label>
                            <input type="text" class="form-control" id="externalEmailsInput" disabled={publishing} bind:value={externalEmails} placeholder={m.iconik_publish_reviewers_externals_placeholder()}>
                        </div>
                        <div class="col-md-4 mb-3">
                            <label class="form-label" for="minApprovalsInput">{m.iconik_publish_min_approvals_label()}</label>
                            <input type="number" min="1" class="form-control" id="minApprovalsInput" disabled={publishing} bind:value={minApprovals}>
                        </div>
                    {/if}
                </div>
                {/if}

            </div>
            <div class="modal-footer">
                {#if publishing}
                <div class="progress w-25" 
                     role="progressbar" 
                     aria-label={m.iconik_publish_progress_label()} 
                     aria-valuenow={progress} 
                     aria-valuemin="0" 
                     aria-valuemax="100">
                    <div class="progress-bar progress-bar-striped progress-bar-animated" style="width: {progress}%"></div>
                </div>
                <p class="text-center small text-muted">{statusMsg} ({progress}%)</p>
                {/if}
                <button type="button" 
                        class="btn btn-primary" 
                        id="publishNowBtn"
                        aria-disabled={publishing}
                        disabled={publishing} 
                        onclick={() => publish()}>{m.iconik_publish_now_button()}</button>
                <button type="button" 
                        class="btn btn-light" 
                        id="cancelPublishBtn"
                        aria-disabled={publishing}
                        disabled={publishing} 
                        onclick={hideModal}>{m.iconik_publish_cancel_button()}</button>
            </div>
        </div>
    </div>
</div>
